
function getEnergyLabel(intensity) {
  if (intensity < 0.3) return "Quiet";
  if (intensity < 0.6) return "Active";
  if (intensity < 0.85) return "Rising";
  return "Strong";
}

const HOT_CITIES = new Set([
  "New York", "Dallas", "Houston", "Detroit",
  "Atlanta", "Los Angeles", "Miami"
]);

function getActivityStyle(intensity, idx, city) {
  const i = Number(intensity || 0);

  const fillColor = HOT_CITIES.has(city)
    ? "#D4AF37"
    : i > 0.75 ? "#D4AF37"
    : i > 0.5  ? "#F59E0B"
    : i > 0.3  ? "#2DD4BF"
    :             "#60A5FA";

  return {
    radius: idx === 0 ? 22 : 8 + Math.pow(i, 1.3) * 24,
    fillColor,
    color: fillColor,
    weight: 0,
    opacity: 1,
    fillOpacity: HOT_CITIES.has(city) ? 0.95 : idx === 0 ? 0.9 : 0.45 + i * 0.4,
    className: idx === 0 ? "heat-glow-strong" : idx < 6 ? "heat-glow-soft" : ""
  };
}


import React, { useEffect, useState } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { AppShell } from "@/components/Nav";
import { track } from "@/lib/analytics";
import { getToken } from "@/lib/auth";

function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const heat = L.heatLayer(points, {
      radius: 44,
      blur: 32,
      maxZoom: 10,
      minOpacity: 0.28,
      max: 1.5,
      gradient: {
        0.2: "#D4AF37",
        0.4: "#F59E0B",
        0.6: "#FBBF24",
        0.8: "#FFD700",
        1.0: "#FFD700"
      },
    }).addTo(map);
    return () => { map.removeLayer(heat); };
  }, [map, points]);
  return null;
}

function computePercentileIntensity(zones) {
  const sorted = [...zones].sort(
    (a, b) => (b.event_count ?? b.user_count ?? 0) - (a.event_count ?? a.user_count ?? 0)
  );
  const n = sorted.length || 1;
  const map = new Map();
  sorted.forEach((z, rank) => {
    const pct = rank / n;
    const intensity =
      pct < 0.10 ? 0.90 :   // Hot   — top 10%
      pct < 0.30 ? 0.65 :   // Strong — next 20%
      pct < 0.70 ? 0.42 :   // Active — next 40%
                   0.15;    // Low   — bottom 30%
    map.set(z.zip || `${z.city}-${z.state}`, intensity);
  });
  return map;
}

function ZoomableMarker({ center, radius, pathOptions, className, children }) {
  const map = useMap();
  return (
    <CircleMarker
      center={center}
      radius={radius}
      pathOptions={pathOptions}
      className={className}
      eventHandlers={{ click: () => map.flyTo(center, 9, { duration: 0.8 }) }}
    >
      {children}
    </CircleMarker>
  );
}

export default function Presence() {
  const [zones, setZones]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    track("presence_page_view");
    const token = getToken();
      fetch("/api/admin/analytics/heatmap", {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => { setZones(data.zones || []); setLoading(false); })
        .catch((e) => { setError("Could not load presence data: " + e); setLoading(false); });
  }, []);

  return (
    <AppShell>
      <div className="p-6 md:p-10 text-white">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">Presence</div>
        <h1 className="font-display text-3xl mb-2">Presence</h1>
        <p className="text-white/40 text-sm mb-1">
          Only area-level activity is shown — never exact user locations.
        </p>
        <p className="text-white/25 text-xs mb-6">
          Showing top U.S. locations by member density.
        </p>

        {loading && <div className="text-white/40 py-10">Loading map…</div>}
        {error   && <div className="text-red-400 text-sm py-4">{error}</div>}

        {!loading && !error && zones.length === 0 && (
          <div className="text-center py-20 text-white/30">
            <div className="text-4xl mb-4">◎</div>
            <div className="text-lg">No active U.S. presence zones yet.</div>
            <div className="text-sm mt-2 text-white/20">Check back as more members join.</div>
          </div>
        )}

        {!loading && !error && zones.length > 0 && (
          <>
            <div
              className="rounded-2xl overflow-hidden border border-white/10"
              style={{ minHeight: "520px" }}
            >
              <MapContainer
                center={[37.8, -96]}
                zoom={4}
                style={{ height: "520px", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution=""
                />
                {/* HeatLayer removed — CircleMarkers only */}
                {(() => {
                  const visible = zones.filter((z) => z.lat && z.lng).slice(0, 8);
                  const imap = computePercentileIntensity(visible);
                  return visible.map((z, idx) => {
                    const zkey = z.zip || `${z.city}-${z.state}`;
                    const intensity = imap.get(zkey) ?? z.intensity;
                    const { radius, className, ...pathOptions } = getActivityStyle(intensity, idx, z.city);
                    const isHot = HOT_CITIES.has(z.city) || idx === 0;
                    const markerClass = [className, isHot ? "hot-pulse" : ""].filter(Boolean).join(" ");
                    return (
                      <ZoomableMarker
                        key={`${zkey}-${idx}`}
                        center={[Number(z.lat), Number(z.lng)]}
                        radius={radius}
                        pathOptions={pathOptions}
                        className={markerClass}
                      >
                        <Tooltip sticky direction="top">
                          <div style={{ minWidth: "130px", lineHeight: "1.7" }}>
                            <div style={{ fontWeight: 600, fontSize: "14px" }}>{z.city}, {z.state}</div>
                            <div style={{ fontSize: "12px", color: "#555" }}>Events: {z.event_count ?? z.user_count ?? "—"}</div>
                            <div style={{ fontSize: "12px", color: "#555" }}>{getEnergyLabel(intensity)}</div>
                          </div>
                        </Tooltip>
                      </ZoomableMarker>
                    );
                  });
                })()}
              </MapContainer>
            </div>

            <div className="flex flex-wrap items-center gap-5 mt-4 text-xs text-white/55">
              <span className="font-medium text-white/75">Activity level:</span>
              {[
                { label: "Low",    color: "#60A5FA" },
                { label: "Active", color: "#2DD4BF" },
                { label: "Strong", color: "#F59E0B" },
                { label: "Hot",    color: "#D4AF37" },
              ].map((l) => (
                <span key={l.label} className="flex items-center gap-1.5">
                  <span
                    className="h-3 w-3 rounded-full inline-block"
                    style={{ background: l.color, boxShadow: `0 0 6px ${l.color}66` }}
                  />
                  {l.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
